import { useState, useEffect, useCallback, useRef } from 'react';
import axios from '../utility/axios';
import { useTableChanges } from './useWebSocket';
import { useAuth } from './useAuth';

const MAILBOX_TABLES = [
    'messages',
    'documents',
    'message_drafts',
    'documents_drafts',
    'message_receivers',
    'files',
    'document',
    'document_drafts',
    'message_draft_files',
    'documenso_files',
    'documenso_files_drafts',
    'attachment_comments',
    'attachment_draft_comments',
    'attachment_documenso_draft_comments'
];

export const useMailbox = () => {
    const { user } = useAuth();
    const [allFolderData, setAllFolderData] = useState({
        inbox: [],
        sent: [],
        drafts: [],
        templates: [],
        deleted: [],
        inbox_documents: [],
        sent_documents: [],
        drafts_documents: [],
        templates_documents: []
    });
    const [folderCounts, setFolderCounts] = useState({
        inbox: 0,
        sent: 0,
        drafts: 0,
        templates: 0,
        deleted: 0,
        inbox_documents: 0,
        sent_documents: 0,
        drafts_documents: 0,
        templates_documents: 0
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const isFetchingRef = useRef(false);
    const userEmailRef = useRef(user?.email);

    useEffect(() => {
        userEmailRef.current = user?.email;
    }, [user?.email]);

    const calculateUnreadCounts = useCallback((folderData) => {
        const counts = {
            inbox: 0,
            sent: 0,
            drafts: folderData.drafts?.length || 0,
            templates: folderData.templates?.length || 0,
            deleted: 0,
            inbox_documents: folderData.inbox_documents?.length || 0,
            sent_documents: 0,
            drafts_documents: folderData.drafts_documents?.length || 0,
            templates_documents: folderData.templates_documents?.length || 0
        };

        if (folderData.inbox) {
            folderData.inbox.forEach(thread => {
                if (thread && !thread.has_been_read) {
                    counts.inbox++;
                }
                if (thread && thread.replies && Array.isArray(thread.replies)) {
                    thread.replies.forEach(reply => {
                        if (!reply.has_been_read) {
                            counts.inbox++;
                        }
                    });
                }
            });
        }

        if (folderData.sent) {
            counts.sent = folderData.sent.filter(email => !email.has_been_read).length;
        }

        if (folderData.sent_documents) {
            counts.sent_documents = folderData.sent_documents.filter(doc => !doc.has_been_read).length;
        }

        if (folderData.deleted) {
            counts.deleted = folderData.deleted.filter(email => !email.has_been_read).length;
        }

        return counts;
    }, []);

    const fetchAllMessages = useCallback(async (silent = false) => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        
        try {
            if (!silent) {
                setLoading(true);
            }
            
            const response = await axios.get('/api/profile/mailbox/');
            
            const newAllFolderData = {
                inbox: response.data.threaded_messages || [],
                sent: response.data.sent_messages || [],
                drafts: response.data.draft_messages || [],
                templates: response.data.templates_messages || [],
                deleted: response.data.deleted_messages || [],
                inbox_documents: response.data.documents || [],
                sent_documents: response.data.sent_documents || [],
                drafts_documents: response.data.draft_documents || [],
                templates_documents: response.data.templates_documents || []
            };
            
            setAllFolderData(newAllFolderData);
            setDataLoaded(true);
            
            const newFolderCounts = calculateUnreadCounts(newAllFolderData);
            setFolderCounts(newFolderCounts);
            
            return newAllFolderData;
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
            setRefreshing(false);
            isFetchingRef.current = false;
        }
    }, [calculateUnreadCounts]);

    const debouncedRefresh = useCallback(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        
        debounceTimeoutRef.current = setTimeout(() => {
            fetchAllMessages(true);
        }, 300);
    }, [fetchAllMessages]);

    const handleTableChange = useCallback((change) => {
        const { table } = change;
        
        if (!MAILBOX_TABLES.includes(table)) return;
        
        debouncedRefresh();
    }, [debouncedRefresh]);

    const { isConnected } = useTableChanges(MAILBOX_TABLES, handleTableChange, true);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        return fetchAllMessages(false);
    }, [fetchAllMessages]);

    useEffect(() => {
        fetchAllMessages();
        
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        allFolderData,
        folderCounts,
        loading,
        refreshing,
        dataLoaded,
        refresh,
        fetchAllMessages,
        isRealtimeConnected: isConnected,
        calculateUnreadCounts
    };
};

export default useMailbox;

